import api from "./api";
import type {
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  Product,
  CreateProductRequest,
  UpdateProductRequest,
  RestaurantStatistics,
  UploadedFile,
  DeleteImageRequest,
} from "../types";

class ManagementService {
  // Login - API PascalCase bekliyor (Admin ile aynı)
  login(email: string, password: string) {
    const payload = { Email: email, Password: password };
    return api().post("/Management/login", payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // Categories
  getCategories() {
    return api().get("/Management/categories");
  }

  createCategory(categoryData: {
    name: string;
    slug?: string;
    description?: string;
    imagePath?: string;
    originalImagePath?: string;
    parentCategoryId?: string;
  }) {
    // Backend C# expects PascalCase property names
    const payload = {
      Name: categoryData.name,
      Slug: categoryData.slug || '',
      Description: categoryData.description || '',
      MainImagePath: categoryData.imagePath,
      OriginalImagePath: categoryData.originalImagePath, // New field
      ParentCategoryId: categoryData.parentCategoryId || null,
    };
    return api().post("/Management/categories/create", payload);
  }

  updateCategory(categoryData: {
    id: string;
    name: string;
    slug?: string;
    description?: string;
    imagePath?: string;
    originalImagePath?: string;
    isActive?: boolean;
  }) {
    // Backend C# expects PascalCase property names
    const payload = {
      Id: categoryData.id,
      Name: categoryData.name,
      Slug: categoryData.slug || '',
      Description: categoryData.description || '',
      MainImagePath: categoryData.imagePath,
      OriginalImagePath: categoryData.originalImagePath, // New field
      IsActive: categoryData.isActive ?? true,
    };
    return api().put("/Management/categories/update", payload);
  }

  deleteCategory(id) {
    return api().delete(`/Management/categories/delete/${id}`);
  }

  toggleCategoryActive(id) {
    return api().put(`/Management/categories/${id}/toggle-active`);
  }

  // Category Reorder (Drag & Drop)
  reorderCategories(items: Array<{ id: string; position: number }>) {
    // Backend C# expects PascalCase property names
    const payload = {
      Items: items.map(item => ({ Id: item.id, Position: item.position }))
    };
    return api().put("/Management/categories/reorder", payload);
  }

  // Products
  getProducts() {
    return api().get("/Management/products");
  }

  createProduct(productData: CreateProductRequest) {
    // Backend C# expects PascalCase
    const payload = {
      Name: productData.name,
      Slug: productData.slug,
      Description: productData.description,
      MainImagePath: productData.mainImagePath,
      OriginalImagePath: productData.originalImagePath,
      ImageList: productData.imageList,
      Price: productData.price,
      DiscountedPrice: productData.discountedPrice,
      CategoryId: productData.categoryId,
      Position: productData.position,
      IsActive: productData.isActive,
      NutritionalInfo: productData.nutritionalInfo // This might need mapping too if nested objects aren't auto-converted, but usually System.Text.Json handles nested camelCase if properly configured OR we might need to map it. For now assuming main properties are the issue.
    };
    return api().post("/Management/products/create", payload);
  }

  updateProduct(productData: UpdateProductRequest) {
    // Backend C# expects PascalCase
    const payload = {
      Id: productData.id,
      Name: productData.name,
      Slug: productData.slug,
      Description: productData.description,
      MainImagePath: productData.mainImagePath,
      OriginalImagePath: productData.originalImagePath,
      ImageList: productData.imageList,
      Price: productData.price,
      DiscountedPrice: productData.discountedPrice,
      CategoryId: productData.categoryId,
      Position: productData.position,
      IsActive: productData.isActive,
      NutritionalInfo: productData.nutritionalInfo
    };
    return api().put("/Management/products/update", payload);
  }

  deleteProduct(id) {
    return api().delete(`/Management/products/delete/${id}`);
  }

  toggleProductActive(id) {
    return api().put(`/Management/products/${id}/toggle-active`);
  }

  // Product Reorder (Drag & Drop)
  reorderProducts(items: Array<{ id: string; position: number }>) {
    // Backend expects: { items: [{ id, position }, ...] }
    return api().put("/Management/products/reorder", { items });
  }

  // Image Upload
  uploadImage(formData) {
    return api().post("/Management/upload-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  uploadImages(formData) {
    return api().post("/Management/upload-images", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  deleteImage(imageData) {
    return api().delete("/Management/delete-image", { data: imageData });
  }

  // Statistics
  getStatistics() {
    return api().get("/Management/statistics");
  }

  // Debug
  getCurrentManager() {
    return api().get("/Management/debug/current-manager");
  }

  getRestaurantData() {
    return api().get("/Management/debug/restaurant-data");
  }

  // Restaurant Management
  getUserRestaurants() {
    return api().get("/Management/user-restaurants");
  }

  // Fallback: Admin endpoint kullanarak restoranları al
  getUserRestaurantsFromAdmin(userId: string) {
    return api().get(`/Admin/managers/${userId}/restaurants`);
  }

  selectRestaurant(restaurantId: string) {
    return api().post("/Management/select-restaurant", { restaurantId });
  }

  getRestaurantDetails() {
    return api().get("/Management/restaurant-details");
  }

  updateRestaurant(restaurantData: any) {
    return api().put("/Management/restaurant/update", restaurantData);
  }

  // ============================================
  // Profile Management
  // ============================================

  // Kullanıcının profil bilgilerini getir
  getProfile() {
    return api().get("/Management/profile");
  }

  // Profil bilgilerini güncelle (Ad, Soyad, Telefon)
  updateProfile(data: { firstName: string; lastName: string; mobile: string }) {
    return api().put("/Management/profile/update", {
      firstName: data.firstName,
      lastName: data.lastName,
      mobile: data.mobile
    });
  }

  // ============================================
  // SMS Password Reset
  // ============================================

  // SMS ile OTP kodu gönder
  requestPasswordSms(phoneNumber: string) {
    return api().post("/Management/password/request-sms", {
      phoneNumber: phoneNumber
    });
  }

  // OTP kodunu doğrula
  verifySmsCode(phoneNumber: string, otpCode: string) {
    return api().post("/Management/password/verify-sms", {
      phoneNumber: phoneNumber,
      otpCode: otpCode
    });
  }

  // Şifreyi değiştir (doğrulanmış token ile)
  changePassword(data: {
    phoneNumber: string;
    verifiedToken: string;
    newPassword: string;
    confirmPassword: string;
  }) {
    return api().put("/Management/password/change", {
      phoneNumber: data.phoneNumber,
      verifiedToken: data.verifiedToken,
      newPassword: data.newPassword,
      confirmPassword: data.confirmPassword
    });
  }

  // ============================================
  // Restaurant Approval Status
  // ============================================

  /**
   * Restoranın onay durumunu getir
   */
  getRestaurantStatus() {
    return api().get("/Restaurant/status");
  }
}

export default new ManagementService();
